/**
 * Remote control keys.
 *
 * The numbers are the panel's, not a standard's. Arrows and Enter match desktop, which is
 * what makes `?tv=1` development in a browser possible at all; everything below Enter is
 * Samsung-specific and has no desktop equivalent.
 *
 * **Back is 10009 and it is the one that matters.** Verified delivered on the target panel
 * (Tizen 9.0) — it needs no `registerKey` call, only `hwkey-event="enable"` in
 * config.xml. An app that does not intercept it is closed by the platform on the first
 * press, which means no back navigation anywhere: from a detail page, from the player,
 * from a category. That is why every handler calls `preventDefault()` unconditionally.
 */
export const KEY = {
  LEFT: 37,
  UP: 38,
  RIGHT: 39,
  DOWN: 40,
  ENTER: 13,
  BACK: 10009,
  PLAY_PAUSE: 10252,
  PLAY: 415,
  PAUSE: 19,
  STOP: 413,
  REWIND: 412,
  FORWARD: 417,
} as const;

// Defined with the scoring rule rather than here: a direction is a geometric idea that
// happens to arrive as a key code, not the other way round.
export type { Direction } from './focus/geometry';
import type { Direction } from './focus/geometry';

export const DIRECTION_BY_KEY: Readonly<Record<number, Direction>> = Object.freeze({
  [KEY.LEFT]: 'left',
  [KEY.UP]: 'up',
  [KEY.RIGHT]: 'right',
  [KEY.DOWN]: 'down',
});

/**
 * Media keys the platform hands over only on request.
 *
 * Arrows, Enter and Back arrive without this. These do not — an unregistered media key
 * goes to whatever the platform thinks is playing, which on a TV is usually the tuner.
 * Registration is best-effort: on the TV browser (as opposed to a packaged widget) the
 * API is absent, and that is not an error worth surfacing.
 */
const MEDIA_KEYS = ['MediaPlayPause', 'MediaPlay', 'MediaPause', 'MediaStop', 'MediaRewind', 'MediaFastForward'];

export function registerMediaKeys(): void {
  const input = window.tizen?.tvinputdevice;
  if (!input) return;
  for (const name of MEDIA_KEYS) {
    try {
      input.registerKey(name);
    } catch {
      // Panels differ in which names they accept, and one refusal must not stop the rest.
    }
  }
}

/**
 * Quits the app.
 *
 * Only ever called from the root of the navigation stack. Outside a widget there is
 * nothing to exit, so it does nothing rather than pretending.
 */
export function exitApp(): void {
  try {
    window.tizen?.application?.getCurrentApplication().exit();
  } catch {
    /* not a widget, or the platform refused — either way there is nothing else to do */
  }
}
